import { env } from "@/shared/config/env";

export type MintErrorCode = "UNAUTHORIZED" | "FORBIDDEN" | "UNAVAILABLE";

export interface EditorTokenPayload {
  editorToken: string;
  expiresAt: string;
  expiresInSec: number;
  audience: string;
}

export type MintResult =
  | { ok: true; data: EditorTokenPayload }
  | { ok: false; code: MintErrorCode; message?: string };

/**
 * Exchange the dashboard's Auth0 access token for a short-lived, store-scoped
 * editor token.
 *
 * Uses plain `fetch`, NOT the app's axios instance — the axios interceptors
 * rewrite the URL with the merchant/system namespace and derive `x-store` from
 * `window.location.pathname`. On the full-page editor route there is no
 * `/store/:id` segment, so that derivation yields nothing. This call must send
 * `x-store` explicitly for the store actually being edited.
 *
 * Never logs either token.
 */
export async function mintEditorToken(
  auth0Token: string,
  storeId: string,
): Promise<MintResult> {
  // env.API_URL already ends with /api/v2 — do not repeat it here.
  const url = `${env.API_URL}/merchant/auth/template-editor/token`;

  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth0Token}`,
        "x-store": storeId,
        "Content-Type": "application/json",
      },
      // No body: the backend resolves the store from x-store and ignores it.
    });
  } catch {
    return {
      ok: false,
      code: "UNAVAILABLE",
      message: "Network error contacting the backend.",
    };
  }

  if (response.status === 401) {
    return {
      ok: false,
      code: "UNAUTHORIZED",
      message: "Dashboard session expired.",
    };
  }
  if (response.status === 403) {
    return {
      ok: false,
      code: "FORBIDDEN",
      message: "You do not have permission to edit this store's template.",
    };
  }
  if (!response.ok) {
    return {
      ok: false,
      code: "UNAVAILABLE",
      message: `Unexpected status ${response.status}.`,
    };
  }

  const body = (await response.json()) as {
    success: boolean;
    message: string;
    data: EditorTokenPayload;
  };
  return { ok: true, data: body.data };
}
